import { Ray } from 'playcanvas';

import { raycastSceneMeshes } from '../collision';
import { Events } from '../events';
import type {
    ReconstructionTimeline,
    ReconstructionTimelineLeadMetrics,
    ReconstructionTimelineScrubState
} from '../reconstruction-timeline';
import type { Scene } from '../scene';
import type { Tour, TourStep } from '../tours';

type PlaybackStatus = 'stopped' | 'playing' | 'paused';

type PlaybackSnapshot = {
    status: PlaybackStatus;
    tourId: string | null;
    stepIndex: number;
    progress: number;
};

type Bookmark = {
    id: string;
    name: string;
};

class PresentationHUD {
    dom: HTMLDivElement;

    private readonly events: Events;

    private readonly stepCounter: HTMLSpanElement;

    private readonly stepLabel: HTMLSpanElement;

    private readonly timelineScrubberWrap: HTMLDivElement;

    private readonly timelineScrubberInput: HTMLInputElement;

    private readonly timelineScrubberTime: HTMLSpanElement;

    private readonly timelineScrubberEvent: HTMLSpanElement;

    private readonly timelineMetrics: HTMLSpanElement;

    private readonly timelineAssumptions: HTMLSpanElement;

    private readonly followButton: HTMLButtonElement;

    private readonly playPauseButton: HTMLButtonElement;

    private readonly stopButton: HTMLButtonElement;

    private readonly prevButton: HTMLButtonElement;

    private readonly nextButton: HTMLButtonElement;

    private readonly homeButton: HTMLButtonElement;

    private readonly exitButton: HTMLButtonElement;

    private readonly laserDot: HTMLDivElement;

    private presenting = false;

    private activeTourId: string | null = null;

    private playbackStatus: PlaybackStatus = 'stopped';

    private stepIndex = -1;

    private laserEnabled = true;

    private laserIdleTimeoutId: ReturnType<typeof setTimeout> | null = null;

    private laserMoveToken = 0;

    private reconstructionTimeline?: ReconstructionTimeline;

    private reconstructionScrubState?: ReconstructionTimelineScrubState;

    private applyingScrubState = false;

    private reconstructionFollowEnabled = false;

    constructor(events: Events) {
        this.events = events;

        this.dom = document.createElement('div');
        this.dom.id = 'presentation-hud';
        this.dom.hidden = true;

        this.laserDot = document.createElement('div');
        this.laserDot.id = 'presentation-laser-dot';
        this.laserDot.hidden = true;
        this.laserDot.setAttribute('aria-hidden', 'true');
        this.dom.appendChild(this.laserDot);

        const bar = document.createElement('div');
        bar.id = 'presentation-hud-bar';

        this.prevButton = this.createButton('presentation-prev-button', 'Prev', () => {
            this.events.fire('tour.prev');
        });

        this.playPauseButton = this.createButton('presentation-play-pause-button', 'Play', () => {
            if (this.playbackStatus === 'playing') {
                this.events.fire('tour.pause');
                return;
            }

            if (this.activeTourId) {
                this.events.fire('tour.play', this.activeTourId);
            }
        });

        this.stopButton = this.createButton('presentation-stop-button', 'Stop', () => {
            this.events.fire('tour.stop');
        });

        this.nextButton = this.createButton('presentation-next-button', 'Next', () => {
            this.events.fire('tour.next');
        });

        this.stepCounter = document.createElement('span');
        this.stepCounter.id = 'presentation-step-counter';

        this.stepLabel = document.createElement('span');
        this.stepLabel.id = 'presentation-step-label';

        this.timelineScrubberWrap = document.createElement('div');
        this.timelineScrubberWrap.id = 'presentation-timeline-scrubber';

        this.timelineScrubberInput = document.createElement('input');
        this.timelineScrubberInput.id = 'presentation-timeline-scrubber-input';
        this.timelineScrubberInput.type = 'range';
        this.timelineScrubberInput.min = '0';
        this.timelineScrubberInput.max = '0';
        this.timelineScrubberInput.step = '0.01';
        this.timelineScrubberInput.value = '0';
        this.timelineScrubberInput.disabled = true;

        this.timelineScrubberTime = document.createElement('span');
        this.timelineScrubberTime.id = 'presentation-timeline-scrubber-time';
        this.timelineScrubberTime.textContent = '0.00s / 0.00s';

        this.timelineScrubberEvent = document.createElement('span');
        this.timelineScrubberEvent.id = 'presentation-timeline-scrubber-event';
        this.timelineScrubberEvent.textContent = 'No timeline';

        this.timelineMetrics = document.createElement('span');
        this.timelineMetrics.id = 'presentation-timeline-metrics';
        this.timelineMetrics.textContent = 'Speed -- world-units/s · Distance -- world-units · Δt -- s';

        this.timelineAssumptions = document.createElement('span');
        this.timelineAssumptions.id = 'presentation-timeline-assumptions';
        this.timelineAssumptions.textContent = 'Assumptions: --';

        this.followButton = this.createButton('presentation-follow-button', 'Follow', () => {
            this.events.fire('reconstructionTimeline.setFollowEnabled', !this.reconstructionFollowEnabled);
        });
        this.followButton.setAttribute('aria-pressed', 'false');

        this.timelineScrubberWrap.append(
            this.timelineScrubberInput,
            this.timelineScrubberTime,
            this.timelineScrubberEvent,
            this.timelineMetrics,
            this.timelineAssumptions
        );

        this.timelineScrubberInput.addEventListener('input', () => {
            if (this.applyingScrubState) {
                return;
            }

            const next = Number.parseFloat(this.timelineScrubberInput.value);
            if (!Number.isFinite(next)) {
                return;
            }

            this.events.fire('reconstructionTimeline.setScrubTime', next);
        });

        this.homeButton = this.createButton('presentation-home-button', 'Home', () => {
            this.panicReset();
        });

        this.exitButton = this.createButton('presentation-exit-button', 'Exit', () => {
            this.exit();
        });

        bar.append(
            this.prevButton,
            this.playPauseButton,
            this.stopButton,
            this.nextButton,
            this.stepCounter,
            this.stepLabel,
            this.timelineScrubberWrap,
            this.followButton,
            this.homeButton,
            this.exitButton
        );

        this.dom.appendChild(bar);

        this.events.on('tour.playbackChanged', (snapshot: PlaybackSnapshot) => {
            this.applyPlaybackSnapshot(snapshot);
        });

        this.events.on('tour.stepChanged', (stepIndex: number, tourId?: string) => {
            if (typeof tourId === 'string') {
                this.activeTourId = tourId;
            }
            this.stepIndex = Number.isInteger(stepIndex) ? stepIndex : -1;
            this.refreshState();
        });

        this.events.on('tour.stopped', () => {
            this.playbackStatus = 'stopped';
            this.stepIndex = -1;
            this.refreshState();
        });

        this.events.on('presentation.enter', (tourId?: string) => {
            this.enter(tourId);
        });

        this.events.on('presentation.exit', () => {
            this.exit();
        });

        this.events.on('presentation.panicReset', () => {
            this.panicReset();
        });

        this.events.on('reconstructionTimeline.changed', (timeline?: ReconstructionTimeline) => {
            this.reconstructionTimeline = timeline;
            this.updateTimelineScrubber();
        });

        this.events.on('reconstructionTimeline.scrubChanged', (state?: ReconstructionTimelineScrubState) => {
            this.reconstructionScrubState = state;
            this.updateTimelineScrubber();
        });

        this.events.on('reconstructionTimeline.followChanged', (enabled?: boolean) => {
            this.reconstructionFollowEnabled = enabled === true;
            this.updateFollowButton();
        });

        window.addEventListener('keydown', this.onKeyDown, true);

        this.reconstructionTimeline = this.events.invoke('reconstructionTimeline.get') as ReconstructionTimeline | undefined;
        this.reconstructionScrubState = this.events.invoke('reconstructionTimeline.scrubState') as ReconstructionTimelineScrubState | undefined;
        this.reconstructionFollowEnabled = this.events.invoke('reconstructionTimeline.followEnabled') === true;

        this.refreshState();
        this.updateTimelineScrubber();
        this.updateFollowButton();
    }

    private updateFollowButton() {
        this.followButton.classList.toggle('is-active', this.reconstructionFollowEnabled);
        this.followButton.setAttribute('aria-pressed', this.reconstructionFollowEnabled ? 'true' : 'false');
    }

    private formatTimelineTime(value: number) {
        return `${value.toFixed(2)}s`;
    }

    private resolveCurrentTimelineEvent() {
        if (!this.reconstructionTimeline || !this.reconstructionScrubState) {
            return null;
        }

        if (this.reconstructionScrubState.eventIndex >= 0 && this.reconstructionScrubState.eventIndex < this.reconstructionTimeline.events.length) {
            return this.reconstructionTimeline.events[this.reconstructionScrubState.eventIndex];
        }

        return null;
    }

    private updateTimelineScrubber() {
        const timeline = this.reconstructionTimeline;
        const scrubState = this.reconstructionScrubState;
        const hasTimeline = !!timeline;

        this.timelineScrubberWrap.hidden = !hasTimeline;
        this.timelineScrubberInput.disabled = !hasTimeline;
        this.followButton.disabled = !hasTimeline;

        if (!timeline) {
            this.timelineScrubberInput.min = '0';
            this.timelineScrubberInput.max = '0';
            this.timelineScrubberInput.value = '0';
            this.timelineScrubberTime.textContent = '0.00s / 0.00s';
            this.timelineScrubberEvent.textContent = 'No timeline';
            this.timelineMetrics.textContent = 'Speed -- world-units/s · Distance -- world-units · Δt -- s';
            this.timelineAssumptions.textContent = 'Assumptions: --';
            return;
        }

        const duration = Math.max(0, timeline.duration);
        const time = scrubState ? scrubState.time : 0;
        const clamped = Math.max(0, Math.min(duration, time));

        this.timelineScrubberInput.min = '0';
        this.timelineScrubberInput.max = `${duration}`;
        this.applyingScrubState = true;
        this.timelineScrubberInput.value = `${clamped}`;
        this.applyingScrubState = false;

        this.timelineScrubberTime.textContent = `${this.formatTimelineTime(clamped)} / ${this.formatTimelineTime(duration)}`;

        const activeEvent = this.resolveCurrentTimelineEvent();
        this.timelineScrubberEvent.textContent = activeEvent ? activeEvent.label : 'No active event';

        const leadMetrics = this.events.invoke('reconstructionTimeline.leadMetrics') as ReconstructionTimelineLeadMetrics | undefined;
        if (!leadMetrics?.available) {
            this.timelineMetrics.textContent = 'Speed -- world-units/s · Distance -- world-units · Δt -- s';
            this.timelineAssumptions.textContent = `Assumptions: ${leadMetrics?.assumptions ?? '--'}`;
            return;
        }

        this.timelineMetrics.textContent = `Speed ${leadMetrics.speed.toFixed(2)} ${leadMetrics.units.speed} · Distance ${leadMetrics.cumulativeDistance.toFixed(2)} ${leadMetrics.units.distance} · Δt ${leadMetrics.deltaTime.toFixed(2)}${leadMetrics.units.time}`;
        this.timelineAssumptions.textContent = `Assumptions: ${leadMetrics.assumptions}`;
    }

    private onKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'F5' && !event.ctrlKey && !event.metaKey && !event.altKey) {
            event.preventDefault();
            if (!this.presenting) {
                this.events.fire('presentation.enter');
            }
            return;
        }

        if (!this.presenting) {
            return;
        }

        const key = event.key;

        if (key === 'Escape') {
            this.consumePresentationKey(event);
            this.exit();
            return;
        }

        if (key === 'ArrowRight' || key === ' ' || key === 'PageDown' || key.toLowerCase() === 'n') {
            this.consumePresentationKey(event);
            this.events.fire('tour.next');
            return;
        }

        if (key === 'ArrowLeft' || key === 'Backspace' || key === 'PageUp' || key.toLowerCase() === 'p') {
            this.consumePresentationKey(event);
            this.events.fire('tour.prev');
            return;
        }

        if (key === 'Home') {
            this.consumePresentationKey(event);
            this.panicReset();
            return;
        }

        if (key.toLowerCase() === 'f') {
            this.consumePresentationKey(event);
            this.toggleFullscreen();
            return;
        }

        if (key.toLowerCase() === 'l') {
            this.consumePresentationKey(event);
            this.toggleLaser();
        }
    };

    private onPointerMove = async (event: PointerEvent) => {
        if (!this.presenting || !this.laserEnabled) {
            return;
        }

        const scene = (window as Window & { scene?: Scene }).scene;
        if (!scene?.camera || !scene.canvas) {
            this.hideLaserDot();
            return;
        }

        const rect = scene.canvas.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
            this.hideLaserDot();
            return;
        }

        const normalizedX = (event.clientX - rect.left) / rect.width;
        const normalizedY = (event.clientY - rect.top) / rect.height;

        if (normalizedX < 0 || normalizedX > 1 || normalizedY < 0 || normalizedY > 1) {
            this.hideLaserDot();
            this.resetLaserIdleTimeout();
            return;
        }

        const moveToken = ++this.laserMoveToken;

        const splatHit = await scene.camera.intersect(normalizedX, normalizedY);

        if (moveToken !== this.laserMoveToken || !this.presenting || !this.laserEnabled) {
            return;
        }

        const meshHit = this.raycastMesh(scene, event.clientX - rect.left, event.clientY - rect.top);

        const nearestHit = this.chooseNearestHit(splatHit, meshHit);
        if (!nearestHit) {
            this.hideLaserDot();
            this.resetLaserIdleTimeout();
            return;
        }

        this.showLaserDot(event.clientX, event.clientY);
        this.resetLaserIdleTimeout();
    };

    private raycastMesh(scene: Scene, screenX: number, screenY: number) {
        const ray = new Ray();
        scene.camera.getRay(screenX, screenY, ray);
        return raycastSceneMeshes(scene, ray);
    }

    private chooseNearestHit(
        splatHit: { distance: number } | null,
        meshHit: { distance: number } | null
    ) {
        if (splatHit && meshHit) {
            return splatHit.distance <= meshHit.distance ? splatHit : meshHit;
        }

        return splatHit ?? meshHit;
    }

    private showLaserDot(clientX: number, clientY: number) {
        this.laserDot.style.left = `${clientX}px`;
        this.laserDot.style.top = `${clientY}px`;
        this.laserDot.hidden = false;
    }

    private hideLaserDot() {
        this.laserDot.hidden = true;
    }

    private resetLaserIdleTimeout() {
        if (this.laserIdleTimeoutId !== null) {
            clearTimeout(this.laserIdleTimeoutId);
        }

        this.laserIdleTimeoutId = window.setTimeout(() => {
            this.hideLaserDot();
        }, 2000);
    }

    private startLaserTracking() {
        window.addEventListener('pointermove', this.onPointerMove, true);
    }

    private stopLaserTracking() {
        window.removeEventListener('pointermove', this.onPointerMove, true);
        if (this.laserIdleTimeoutId !== null) {
            clearTimeout(this.laserIdleTimeoutId);
            this.laserIdleTimeoutId = null;
        }
        this.hideLaserDot();
    }

    private toggleLaser() {
        this.laserEnabled = !this.laserEnabled;

        if (!this.presenting) {
            this.hideLaserDot();
            return;
        }

        if (this.laserEnabled) {
            this.startLaserTracking();
            return;
        }

        this.stopLaserTracking();
    }

    private consumePresentationKey(event: KeyboardEvent) {
        event.preventDefault();
        event.stopPropagation();
    }

    private async toggleFullscreen() {
        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen();
                return;
            }

            await document.documentElement.requestFullscreen();
        } catch {
            // no-op
        }
    }

    private createButton(id: string, text: string, onClick: () => void) {
        const button = document.createElement('button');
        button.id = id;
        button.className = 'presentation-hud-button';
        button.type = 'button';
        button.textContent = text;
        button.addEventListener('click', onClick);
        return button;
    }

    private getTours() {
        const tours = this.events.invoke('tours.list');
        return Array.isArray(tours) ? tours as Tour[] : [];
    }

    private getBookmarks() {
        const bookmarks = this.events.invoke('bookmarks.list');
        return Array.isArray(bookmarks) ? bookmarks as Bookmark[] : [];
    }

    private resolveTourId(requestedTourId?: string) {
        const tours = this.getTours();
        if (typeof requestedTourId === 'string' && tours.some(tour => tour.id === requestedTourId)) {
            return requestedTourId;
        }

        const firstWithSteps = tours.find(tour => tour.steps.length > 0);
        return firstWithSteps?.id ?? tours[0]?.id ?? null;
    }

    private getActiveTour() {
        if (!this.activeTourId) {
            return null;
        }

        return this.getTours().find(tour => tour.id === this.activeTourId) ?? null;
    }

    private getStepLabelText(step: TourStep | undefined) {
        if (!step) {
            return 'No active step';
        }

        if (step.label && step.label.trim().length > 0) {
            return step.label;
        }

        if (step.type === 'bookmark') {
            const bookmark = this.getBookmarks().find(item => item.id === step.bookmarkId);
            return bookmark?.name ?? 'Bookmark step';
        }

        return 'Custom view';
    }

    private applyPlaybackSnapshot(snapshot: PlaybackSnapshot) {
        if (!snapshot || typeof snapshot !== 'object') {
            return;
        }

        const status = snapshot.status;
        if (status === 'playing' || status === 'paused' || status === 'stopped') {
            this.playbackStatus = status;
        }

        if (typeof snapshot.tourId === 'string') {
            this.activeTourId = snapshot.tourId;
        } else if (snapshot.tourId === null && this.playbackStatus === 'stopped') {
            this.activeTourId = this.activeTourId ?? null;
        }

        this.stepIndex = Number.isInteger(snapshot.stepIndex) ? snapshot.stepIndex : -1;
        this.refreshState();
    }

    private refreshState() {
        const activeTour = this.getActiveTour();
        const stepCount = activeTour?.steps.length ?? 0;
        const isStepValid = this.stepIndex >= 0 && this.stepIndex < stepCount;

        if (stepCount === 0) {
            this.stepCounter.textContent = 'No tour loaded';
            this.stepLabel.textContent = 'Select or pass a tour to present';
        } else {
            this.stepCounter.textContent = isStepValid ? `Step ${this.stepIndex + 1} of ${stepCount}` : `Step 1 of ${stepCount}`;
            this.stepLabel.textContent = this.getStepLabelText(isStepValid ? activeTour?.steps[this.stepIndex] : activeTour?.steps[0]);
        }

        this.playPauseButton.textContent = this.playbackStatus === 'playing' ? 'Pause' : 'Play';

        const controlsDisabled = stepCount === 0;
        this.playPauseButton.disabled = controlsDisabled;
        this.stopButton.disabled = this.playbackStatus === 'stopped';
        this.prevButton.disabled = controlsDisabled;
        this.nextButton.disabled = controlsDisabled;
        this.homeButton.disabled = controlsDisabled;
    }

    private panicReset() {
        if (!this.presenting) {
            this.events.fire('camera.reset');
            return;
        }

        const tourId = this.activeTourId;
        const activeTour = this.getActiveTour();

        if (!tourId || !activeTour || activeTour.steps.length === 0) {
            this.events.fire('camera.reset');
            return;
        }

        this.events.fire('tour.stop');
        this.events.fire('tour.play', tourId);
        this.events.fire('tour.pause');
    }

    private enter(tourId?: string) {
        const resolvedTourId = this.resolveTourId(tourId);

        this.presenting = true;
        this.dom.hidden = false;
        this.activeTourId = resolvedTourId;
        this.events.fire('presentation.modeChanged', true);

        if (this.laserEnabled) {
            this.startLaserTracking();
        } else {
            this.hideLaserDot();
        }

        if (resolvedTourId) {
            this.events.fire('tour.play', resolvedTourId);
        } else {
            this.playbackStatus = 'stopped';
            this.stepIndex = -1;
            this.refreshState();
        }
    }

    private exit() {
        if (!this.presenting) {
            return;
        }

        this.presenting = false;
        this.stopLaserTracking();
        this.dom.hidden = true;
        this.events.fire('tour.stop');
        this.events.fire('presentation.modeChanged', false);
    }
}

export { PresentationHUD };
